import CtaBanner from "@/components/site/CtaBanner";
import { CheckCircle2, Circle } from "lucide-react";

const INCLUDED = [
  "Commercial solar system",
  "Battery storage where suitable",
  "System design",
  "Installation coordination",
  "Monitoring",
  "Maintenance",
  "Usage-based billing",
  "Ongoing support",
];

const PAY = [
  "No upfront system purchase",
  "Agreed cents-per-kWh rate",
  "Only for energy used from the system",
  "Grid energy remains billed by your normal retailer",
];

const AFFECTS = [
  "Energy usage",
  "Roof size and condition",
  "Operating hours",
  "Battery suitability",
  "Network requirements",
  "Credit approval",
  "PPA term",
  "Site access and ownership structure",
];

const BEST_FIT = [
  "Annual electricity usage over a certain threshold",
  "Stable business operations",
  "Suitable roof or land area",
  "Long-term site occupancy",
  "Commercial electricity bills available",
  "Strong daytime or extended-hours energy usage",
];

export default function BusinessEnergyPlan() {
  return (
    <div>
      <section className="container-x mx-auto max-w-7xl pt-14 md:pt-24 pb-12">
        <div className="max-w-3xl">
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-4">Be Green Business Energy Plan</p>
          <h1 className="font-heading text-4xl md:text-6xl font-semibold text-forest leading-[1.03] tracking-tight" data-testid="bep-title">
            A commercial solar and battery plan, without buying the system.
          </h1>
          <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
            Designed for Australian businesses that want cleaner energy without purchasing the
            system upfront. We install, own and maintain — you pay per kWh used from the system.
          </p>
        </div>
      </section>

      <section className="container-x mx-auto max-w-7xl grid gap-6 md:grid-cols-2 pb-6 md:pb-10" data-testid="bep-cards-top">
        <Card title="What is included">
          <List items={INCLUDED} />
        </Card>
        <Card title="What the customer pays">
          <List items={PAY} accent />
        </Card>
      </section>

      <section className="container-x mx-auto max-w-7xl grid gap-6 md:grid-cols-2 pb-10" data-testid="bep-cards-bottom">
        <Card title="What affects the proposal">
          <List items={AFFECTS} muted />
        </Card>
        <Card title="Best-fit businesses">
          <List items={BEST_FIT} />
        </Card>
      </section>

      <section className="bg-mist py-16 md:py-24">
        <div className="container-x mx-auto max-w-7xl grid lg:grid-cols-[1.15fr_1fr] gap-14 items-start">
          <div>
            <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">How you pay</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05]">
              One flat rate per kWh used from the system. That’s it.
            </h2>
            <p className="mt-5 text-neutral-600 text-[16px] leading-relaxed max-w-xl">
              Your business keeps its existing electricity retailer for grid supply. Be Green Group
              only bills you for the energy consumed from the on-site solar and battery system,
              measured through a dedicated meter or agreed measurement method.
            </p>
          </div>
          <div className="rounded-3xl bg-white p-8 border border-black/5 shadow-sm">
            <p className="text-[12px] tracking-widest uppercase text-neutral-500 font-semibold">Sample PPA structure (indicative)</p>
            <div className="mt-4 grid gap-3">
              <Row label="On-site energy from Be Green system" value="Flat cents / kWh" />
              <Row label="Grid electricity" value="Billed by your existing retailer" />
              <Row label="System ownership" value="Be Green Group" />
              <Row label="System maintenance" value="Be Green Group" />
              <Row label="Term" value="Typically 7 – 15 years" />
            </div>
            <p className="mt-5 text-[12.5px] text-neutral-500 leading-relaxed">
              Indicative only. Rate, term and structure depend on site assessment, engineering,
              network requirements, credit approval and final agreement.
            </p>
          </div>
        </div>
      </section>

      <CtaBanner
        testId="bep-cta"
        title="Apply for a business energy assessment"
        body="Share your bills and site details — our team will assess your fit for a no-upfront commercial PPA."
        ctaLabel="Apply for an assessment"
      />
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-7 md:p-8">
      <h3 className="font-heading text-xl md:text-2xl font-semibold text-forest">{title}</h3>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function List({ items, accent = false, muted = false }) {
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-[15px] text-neutral-700">
          {accent ? (
            <span className="mt-0.5 h-6 w-6 rounded-full bg-bright/15 grid place-items-center text-forest">
              <CheckCircle2 size={14} strokeWidth={2.4}/>
            </span>
          ) : muted ? (
            <span className="mt-0.5 h-6 w-6 rounded-full bg-mist grid place-items-center text-forest">
              <Circle size={10} strokeWidth={3}/>
            </span>
          ) : (
            <span className="mt-0.5 h-6 w-6 rounded-full bg-forest/10 grid place-items-center text-forest">
              <CheckCircle2 size={14} strokeWidth={2.4}/>
            </span>
          )}
          {it}
        </li>
      ))}
    </ul>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-black/5 last:border-b-0">
      <span className="text-[14.5px] text-neutral-600">{label}</span>
      <span className="text-[14.5px] font-semibold text-forest text-right">{value}</span>
    </div>
  );
}
